import { EMPTY_OBJECT } from "@/lib/utils/stable-empty";
import { useState, useCallback } from "react";

export interface UseModalManagerProps<T = any> {
  onModalClose?: () => void;
}

export interface UseModalManagerReturn<T = any> {
  showModal: boolean;
  onSuccessCallback: ((data: T) => void) | null;
  handleAddNew: (onSuccess: (data: T) => void) => void;
  handleItemCreated: (data: T) => void;
  handleModalClose: () => void;
}

/**
 * Shared hook for managing modal state and callbacks
 * Used by select components that have "Add New" functionality
 */
export function useModalManager<T = any>(
  props: UseModalManagerProps<T> = EMPTY_OBJECT
): UseModalManagerReturn<T> {
  const [showModal, setShowModal] = useState(false);
  const [onSuccessCallback, setOnSuccessCallback] = useState<((data: T) => void) | null>(null);

  const handleAddNew = useCallback((onSuccess: (data: T) => void) => {
    setOnSuccessCallback(() => onSuccess);
    setShowModal(true);
  }, []);

  const handleItemCreated = useCallback(
    (data: T) => {
      if (onSuccessCallback) {
        onSuccessCallback(data);
      }
      setShowModal(false);
      setOnSuccessCallback(null);
      props.onModalClose?.();
    },
    [onSuccessCallback, props]
  );

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setOnSuccessCallback(null);
    props.onModalClose?.();
  }, [props]);

  return {
    showModal,
    onSuccessCallback,
    handleAddNew,
    handleItemCreated,
    handleModalClose,
  };
}
